// Google Analytics 4 Measurement ID
// Replace 'G-XXXXXXXXXX' with your actual GA4 Measurement ID
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-XXXXXXXXXX';

// Check if we should track analytics (exclude development/localhost)
const shouldTrack = () => {
  const isLocalhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' || 
                     window.location.hostname.includes('localhost');
  
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  return !isLocalhost && !isDevelopment;
};

// Get gtag function from window object
const gtag = (...args) => {
  if (window.gtag && shouldTrack()) {
    window.gtag(...args);
  }
};

// Initialize Google Analytics
export const initGA = () => {
  // Only initialize if tracking is enabled and not in development
  if (shouldTrack()) {
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
    });
  }
};

// Extract UTM parameters from URL
const getUtmParameters = () => {
  const params = new URLSearchParams(window.location.search);
  const utmParams = {};
  
  // Standard UTM parameters
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
    const value = params.get(param);
    if (value) {
      utmParams[param] = value;
    }
  });
  
  return utmParams;
};

// Track page views
export const trackPageView = (path, title) => {
  if (shouldTrack()) {
    const utmParams = getUtmParameters();
    gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title,
      ...utmParams
    });
  }
};

// E-commerce tracking events
export const trackEvent = (action, parameters = {}) => {
  if (shouldTrack()) {
    gtag('event', action, parameters);
  }
};

// Track product views
export const trackProductView = (productData) => {
  trackEvent('view_item', {
    currency: 'USD',
    value: productData.price,
    items: [{
      item_id: productData.id,
      item_name: productData.name,
      item_category: productData.collection,
      price: productData.price,
      quantity: 1
    }]
  });
};

// Track add to cart
export const trackAddToCart = (productData, quantity = 1) => {
  trackEvent('add_to_cart', {
    currency: 'USD',
    value: productData.price * quantity,
    items: [{
      item_id: productData.id,
      item_name: productData.name,
      item_category: productData.collection,
      price: productData.price,
      quantity: quantity
    }]
  });
};

// Track cart view
export const trackViewCart = (cartItems) => {
  const items = cartItems.map(item => ({
    item_id: item.id,
    item_name: item.name,
    item_category: item.collection,
    price: item.price,
    quantity: item.quantity
  }));

  const totalValue = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);

  trackEvent('view_cart', {
    currency: 'USD',
    value: totalValue,
    items: items
  });
};

// Track begin checkout
export const trackBeginCheckout = (cartItems) => {
  const items = cartItems.map(item => ({
    item_id: item.id,
    item_name: item.name,
    item_category: item.collection,
    price: item.price,
    quantity: item.quantity
  }));

  const totalValue = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);

  trackEvent('begin_checkout', {
    currency: 'USD',
    value: totalValue,
    items: items
  });
};

// Track purchase completion
export const trackPurchase = (orderData) => {
  trackEvent('purchase', {
    transaction_id: orderData.transactionId,
    currency: 'USD',
    value: orderData.total,
    items: orderData.items.map(item => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.collection,
      price: item.price,
      quantity: item.quantity
    }))
  });
};

// Track blog engagement
export const trackBlogView = (blogPost) => {
  trackEvent('blog_view', {
    article_title: blogPost.title,
    article_category: blogPost.category,
    article_author: blogPost.author,
    engagement_time_msec: 0 // Will be updated on scroll
  });
};

// Track blog engagement time
export const trackBlogEngagement = (blogPost, engagementTime) => {
  trackEvent('blog_engagement', {
    article_title: blogPost.title,
    article_category: blogPost.category,
    engagement_time_msec: engagementTime * 1000
  });
};

// Track search
export const trackSearch = (searchTerm, resultsCount) => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount
  });
};

// Track newsletter signup
export const trackNewsletterSignup = (source) => {
  trackEvent('newsletter_signup', {
    source: source // 'blog', 'homepage', 'product_page', etc.
  });
};

// Track social sharing
export const trackSocialShare = (platform, contentType, contentId) => {
  trackEvent('share', {
    method: platform,
    content_type: contentType,
    item_id: contentId
  });
};

// Custom events for Lumi-specific tracking
export const trackWellnessPageView = (pageName) => {
  trackEvent('wellness_page_view', {
    page_name: pageName
  });
};

export const trackProductInteraction = (productId, interactionType) => {
  trackEvent('product_interaction', {
    item_id: productId,
    interaction_type: interactionType // 'learn_more', 'view_specs', 'compare', etc.
  });
};

// UTM URL Builder for campaign tracking
export const buildUtmUrl = (baseUrl, utmParams) => {
  const url = new URL(baseUrl, window.location.origin);
  
  Object.entries(utmParams).forEach(([key, value]) => {
    if (value && key.startsWith('utm_')) {
      url.searchParams.set(key, value);
    }
  });
  
  return url.toString();
};

// Track campaign link clicks
export const trackCampaignClick = (campaignName, source, medium, content = null) => {
  trackEvent('campaign_click', {
    campaign_name: campaignName,
    campaign_source: source,
    campaign_medium: medium,
    campaign_content: content
  });
};

// Enhanced traffic source tracking to help resolve "Unassigned" traffic
export const trackTrafficSource = () => {
  if (shouldTrack()) {
    const referrer = document.referrer;
    const hostname = window.location.hostname;
    const search = window.location.search;
    
    // Get manual channel classification
    let channelGroup = 'Direct';
    if (referrer) {
      const referrerDomain = new URL(referrer).hostname;
      if (referrerDomain.includes('google')) {
        channelGroup = 'Organic Search';
      } else if (referrerDomain.includes('bing') || referrerDomain.includes('yahoo')) {
        channelGroup = 'Organic Search';
      } else if (referrerDomain.includes('facebook') || referrerDomain.includes('instagram') || referrerDomain.includes('twitter')) {
        channelGroup = 'Social';
      } else if (referrerDomain !== hostname) {
        channelGroup = 'Referral';
      }
    }
    
    // Track custom event with detailed source information
    trackEvent('traffic_source_detail', {
      referrer: referrer || '(direct)',
      referrer_domain: referrer ? new URL(referrer).hostname : '(direct)',
      channel_group_manual: channelGroup,
      has_utm_params: search.includes('utm_'),
      user_agent: navigator.userAgent.substring(0, 100) // Truncated for privacy
    });
  }
};